// ─────────────────────────────────────────────────────────────────────────────
// IT Services / Web Projects / Marketing & Advertising — React Query Hooks
//
// Follows the same pattern as useQuotations.ts, useProjects.ts, useDeals.ts
// Uses @tanstack/react-query with proper cache invalidation
// ─────────────────────────────────────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { servicesProjectsApi, servicesProjectsQueryKeys } from '../api';
import { api } from '@/shared/api/axios';

// ═══════════════════════════════════════════════════════════════════════════════
// RATE CARDS
// ═══════════════════════════════════════════════════════════════════════════════

export function useRateCards(filters: { status?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.rateCardsList(filters),
    queryFn: () => servicesProjectsApi.listRateCards(filters),
    placeholderData: keepPreviousData,
  });
}

export function useActiveRateCard() {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.activeRateCard(),
    queryFn: () => servicesProjectsApi.getActiveRateCard(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRateCard(id: string | undefined) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.rateCard(id ?? ''),
    queryFn: () => servicesProjectsApi.getRateCard(id!),
    enabled: !!id,
  });
}

export function useRateCardMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.rateCards() });

  const create = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createRateCard(payload),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateRateCard(id, payload),
    onSuccess: invalidate,
  });

  const activate = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.activateRateCard(id),
    onSuccess: invalidate,
  });

  return { create, update, activate };
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROPOSALS
// ══════════════════════════════════════════════════════════════════════════════

export function useProposals(filters: { status?: string; clientId?: string; dealId?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.proposalsList(filters),
    queryFn: () => servicesProjectsApi.listProposals(filters),
    placeholderData: keepPreviousData,
  });
}

export function useProposal(id: string | undefined) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.proposal(id ?? ''),
    queryFn: () => servicesProjectsApi.getProposal(id!),
    enabled: !!id,
  });
}

export function useProposalMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.proposals() });

  const create = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createProposal(payload),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateProposal(id, payload),
    onSuccess: invalidate,
  });

  const send = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.sendProposal(id),
    onSuccess: invalidate,
  });

  const accept = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.acceptProposal(id),
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => servicesProjectsApi.rejectProposal(id, reason),
    onSuccess: invalidate,
  });

  const convertToContract = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.convertProposalToContract(id, payload),
    onSuccess: invalidate,
  });

  return { create, update, send, accept, reject, convertToContract };
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTRACTS
// ══════════════════════════════════════════════════════════════════════════════

export function useContracts(filters: { status?: string; clientId?: string; proposalId?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.contractsList(filters),
    queryFn: () => servicesProjectsApi.listContracts(filters),
    placeholderData: keepPreviousData,
  });
}

export function useContract(id: string | undefined) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.contract(id ?? ''),
    queryFn: () => servicesProjectsApi.getContract(id!),
    enabled: !!id,
  });
}

export function useContractMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.contracts() });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateContract(id, payload),
    onSuccess: invalidate,
  });

  const activate = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.activateContract(id),
    onSuccess: invalidate,
  });

  return { update, activate };
}

// ══════════════════════════════════════════════════════════════════════════════
// PROJECTS
// ═════════════════════════════════════════════════════════════════════════════

export function useProjects(filters: { status?: string; contractId?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.projectsList(filters),
    queryFn: () => servicesProjectsApi.listProjects(filters),
    placeholderData: keepPreviousData,
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.project(id ?? ''),
    queryFn: () => servicesProjectsApi.getProject(id!),
    enabled: !!id,
  });
}

export function useProjectMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.projects() });

  const createProject = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createProject(payload),
    onSuccess: invalidate,
  });

  const updateProject = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateProject(id, payload),
    onSuccess: invalidate,
  });

  const deleteProject = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.deleteProject(id),
    onSuccess: invalidate,
  });

  return { createProject, updateProject, deleteProject };
}

// ═════════════════════════════════════════════════════════════════════════════
// CAMPAIGNS
// ════════════════════════════════════════════════════════════════════════════

export function useCampaigns(filters: { status?: string; contractId?: string; projectId?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.campaignsList(filters),
    queryFn: () => servicesProjectsApi.listCampaigns(filters),
    placeholderData: keepPreviousData,
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.campaign(id ?? ''),
    queryFn: () => servicesProjectsApi.getCampaign(id!),
    enabled: !!id,
  });
}

export function useCampaignMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.campaigns() });

  const createCampaign = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createCampaign(payload),
    onSuccess: invalidate,
  });

  const updateCampaign = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateCampaign(id, payload),
    onSuccess: invalidate,
  });

  const deleteCampaign = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.deleteCampaign(id),
    onSuccess: invalidate,
  });

  return { createCampaign, updateCampaign, deleteCampaign };
}

// ═════════════════════════════════════════════════════════════════════════════
// TIMESHEETS
// ════════════════════════════════════════════════════════════════════════════

export function useTimesheets(filters: { projectId?: string; userId?: string; status?: string; dateFrom?: string; dateTo?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.timesheetsList(filters),
    queryFn: () => servicesProjectsApi.listTimesheets(filters),
    placeholderData: keepPreviousData,
  });
}

export function useTimesheetMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.timesheets() });

  const createTimesheet = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createTimesheet(payload),
    onSuccess: invalidate,
  });

  const updateTimesheet = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateTimesheet(id, payload),
    onSuccess: invalidate,
  });

  const deleteTimesheet = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.deleteTimesheet(id),
    onSuccess: invalidate,
  });

  return { createTimesheet, updateTimesheet, deleteTimesheet };
}

// ═════════════════════════════════════════════════════════════════════════════
// BILLING
// ════════════════════════════════════════════════════════════════════════════

export function useInvoices(filters: { status?: string; projectId?: string; clientId?: string; billingType?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.invoicesList(filters),
    queryFn: () => servicesProjectsApi.listInvoices(filters),
    placeholderData: keepPreviousData,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.invoice(id ?? ''),
    queryFn: () => servicesProjectsApi.getInvoice(id!),
    enabled: !!id,
  });
}

export function useInvoiceMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.billing() });

  const createInvoice = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createInvoice(payload),
    onSuccess: invalidate,
  });

  const updateInvoice = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateInvoice(id, payload),
    onSuccess: invalidate,
  });

  const deleteInvoice = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.deleteInvoice(id),
    onSuccess: invalidate,
  });

  const sendInvoice = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.sendInvoice(id),
    onSuccess: invalidate,
  });

  const markPaid = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.markInvoicePaid(id),
    onSuccess: invalidate,
  });

  return { createInvoice, updateInvoice, deleteInvoice, sendInvoice, markPaid };
}

// ════════════════════════════════════════════════════════════════════════════
// REVENUE RECOGNITION
// ════════════════════════════════════════════════════════════════════════════

export function useRevenueRecognition(filters: { status?: string; projectId?: string; contractId?: string; method?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.revenueRecognitionList(filters),
    queryFn: () => servicesProjectsApi.listRevenueRecognition(filters),
    placeholderData: keepPreviousData,
  });
}

export function useRevenueRecognitionMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.revenueRecognition() });

  const createRecognition = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createRevenueRecognition(payload),
    onSuccess: invalidate,
  });

  const updateRecognition = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateRevenueRecognition(id, payload),
    onSuccess: invalidate,
  });

  const runRecognition = useMutation({
    mutationFn: (id: string) => servicesProjectsApi.runRevenueRecognition(id),
    onSuccess: invalidate,
  });

  const recognizeItem = useMutation({
    mutationFn: ({ id, itemId }: { id: string; itemId: string }) => servicesProjectsApi.recognizeRevenueItem(id, itemId),
    onSuccess: invalidate,
  });

  return { createRecognition, updateRecognition, runRecognition, recognizeItem };
}

// ═════════════════════════════════════════════════════════════════════════════
// PROFITABILITY
// ════════════════════════════════════════════════════════════════════════════

export function useProfitability(filters: { projectId?: string; period?: string; periodType?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.profitabilityList(filters),
    queryFn: () => servicesProjectsApi.listProfitability(filters),
    placeholderData: keepPreviousData,
  });
}

export function useProfitabilityMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.profitability() });

  const createProfitability = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createProfitability(payload),
    onSuccess: invalidate,
  });

  const updateProfitability = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateProfitability(id, payload),
    onSuccess: invalidate,
  });

  const computeProfitability = useMutation({
    mutationFn: ({ projectId, payload }: { projectId: string; payload: any }) => servicesProjectsApi.computeProfitability(projectId, payload),
    onSuccess: invalidate,
  });

  return { createProfitability, updateProfitability, computeProfitability };
}

// ════════════════════════════════════════════════════════════════════════════
// EXPENSE ALLOCATIONS
// ════════════════════════════════════════════════════════════════════════════

export function useExpenseAllocations(filters: { projectId?: string; category?: string; status?: string } = {}) {
  return useQuery({
    queryKey: servicesProjectsQueryKeys.expenseAllocationsList(filters),
    queryFn: () => servicesProjectsApi.listExpenseAllocations(filters),
    placeholderData: keepPreviousData,
  });
}

export function useExpenseAllocationMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesProjectsQueryKeys.expenseAllocations() });

  const createExpenseAllocation = useMutation({
    mutationFn: (payload: any) => servicesProjectsApi.createExpenseAllocation(payload),
    onSuccess: invalidate,
  });

  const updateExpenseAllocation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => servicesProjectsApi.updateExpenseAllocation(id, payload),
    onSuccess: invalidate,
  });

  return { createExpenseAllocation, updateExpenseAllocation };
}

// ═════════════════════════════════════════════════════════════════════════════
// JOURNAL ENTRIES (from Accounting)
// ════════════════════════════════════════════════════════════════════════════

export function useJournalEntries(filters: { sourceType?: string; status?: string; dateFrom?: string; dateTo?: string } = {}) {
  const params = new URLSearchParams({ limit: '200' });
  if (filters.status) params.set('status', filters.status);
  if (filters.dateFrom) params.set('startDate', filters.dateFrom);
  if (filters.dateTo) params.set('endDate', filters.dateTo);

  return useQuery({
    queryKey: ['journal-entries', filters],
    queryFn: async () => {
      // Journals live at /api/accounts/journal-vouchers (/journal-entries never
      // existed and 404'd). Map them to the narration/lines shape these pages read.
      const { data } = await api.get(`/api/accounts/journal-vouchers?${params}`);
      const rows: any[] = Array.isArray(data?.data) ? data.data : [];
      return rows
        .map((j) => ({
          ...j,
          narration: j.description || j.reference || '',
          lines: (j.entries || []).map((e: any) => ({ ...e, accountType: e.accountId?.type })),
        }))
        .filter((j) => !filters.sourceType || j.sourceType === filters.sourceType);
    },
    placeholderData: keepPreviousData,
  });
}

// ═════════════════════════════════════════════════════════════════════════════
// HEALTH
// ═════════════════════════════════════════════════════════════════════════════

export function useHealthCheck() {
  return useQuery({
    queryKey: [...servicesProjectsQueryKeys.root, 'health'],
    queryFn: () => servicesProjectsApi.health(),
    staleTime: 30 * 1000,
  });
}